import { getReceipt } from '@/api';
import DetailBillModal from '@/components/Airport/DetailBillModal';
import ReceiptLayout from '@/components/Receipt/ReceiptLayout';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import { IMG_RECEIPT_PREVIEW2 } from '@repo/assets';
import { FlexWrap, ReceiptSummary } from '@repo/components';
import { Icon } from '@successmode-ktp/kui';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

const Receipt = () => {
  const { nationality, passportNumber, refundAfter } = useAppSelector(
    (state) => state,
  ) as IGlobalState;
  const location = useLocation<{ refundAmount: number }>();
  const refundAmount = location.state ? location.state.refundAmount : 0;

  const [openedBillIndex, setOpenedBillIndex] = useState(-1);
  const [isLatest, setIsLatest] = useState<'LATEST' | 'PAST'>('LATEST');

  const { data = [] } = useQuery(['getStoreReceipt'], () =>
    getReceipt({
      passportNumber,
      latest: true,
      refundAfter,
    }),
  );

  return (
    <ReceiptLayout hasPadding={false}>
      <HeaderWrapper>
        <button type='button' onClick={() => window.history.back()}>
          <Icon.ChevronLeft />
        </button>
        <Title>My Refund</Title>
        <EmptyBox />
      </HeaderWrapper>
      <RefundAmountWrapper>
        <RefundAmountTitle>My Refund</RefundAmountTitle>
        <RefundAmount>
          {refundAmount.toLocaleString()} <Currency>KRW</Currency>
        </RefundAmount>
      </RefundAmountWrapper>
      <DivideLine />
      <ReceiptWrapper>
        <FilterWrapper>
          <SubTitle>My Refund Receipts</SubTitle>
          <FilterContainer>
            <FilterBtn
              $isActive={isLatest === 'LATEST'}
              onClick={() => setIsLatest('LATEST')}
            >
              Latest
            </FilterBtn>
            <FilterDivider>|</FilterDivider>
            <FilterBtn
              $isActive={isLatest === 'PAST'}
              onClick={() => setIsLatest('PAST')}
            >
              Past
            </FilterBtn>
          </FilterContainer>
        </FilterWrapper>
        <ReceiptList isLatest={isLatest === 'LATEST'}>
          {data.map((receipt, index) => {
            const dateOfPurchase = receipt?.saleDate.split(' ')[0] || '-';
            const payment =
              Number(receipt?.totalAmount).toLocaleString() || '0';
            const refund = Number(receipt?.totalRefund).toLocaleString() || '0';
            // const receiptData = reformReceiptData(receipt, passportNumber!);
            return (
              <ReceiptSummary
                key={index}
                nationality={nationality}
                refundIndex={receipt.refundIndex}
                dateOfPurchase={dateOfPurchase}
                name={receipt.franchiseeName || '-'}
                payment={payment}
                refund={refund}
                imgSrc={IMG_RECEIPT_PREVIEW2}
                btnTitle={
                  nationality === 'KOR' ? '영수증 보기' : 'View Receipt'
                }
                onClick={() => setOpenedBillIndex(index)}
              />
            );
          })}
        </ReceiptList>
      </ReceiptWrapper>
      {openedBillIndex > -1 && data && (
        <DetailBillModal
          billList={data}
          openedId={openedBillIndex}
          onClose={() => setOpenedBillIndex(-1)}
        />
      )}
    </ReceiptLayout>
  );
};

export default Receipt;

const HeaderWrapper = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 20px;
  box-shadow: 0 4px 4px 0 rgba(216, 216, 216, 0.25);
`;

const EmptyBox = styled.div`
  width: 24px;
  height: 24px;
`;

const Title = styled.h2`
  color: var(--KTP-Grayscale-Gray_02, #111);
  font-size: 20px;
  font-weight: 700;
  line-height: 140%; /* 28px */
  letter-spacing: -0.4px;
`;

const ReceiptWrapper = styled.div`
  padding: 0 20px;
  width: 100%;
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 17px;
`;

const SubTitle = styled.p`
  color: var(--KTP-Grayscale-Gray_04, #666);
  font-size: 14px;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
  word-break: keep-all;
  white-space: nowrap;
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterBtn = styled.button<{ $isActive: boolean }>`
  color: ${(props) => (props.$isActive ? '#000000' : '#9FA1A7')};
  font-size: 14px;
`;

const FilterDivider = styled.span`
  color: #9fa1a7;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const RefundAmountWrapper = styled.dl`
  width: 100%;
  padding: 30px 20px;
`;

const RefundAmountTitle = styled.dt`
  color: var(--KTP-Grayscale-Gray_04, #666);
  font-size: 14px;
  font-weight: 600;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const RefundAmount = styled.dl`
  color: var(--KTP-Grayscale-Gray_01, #000);
  font-size: 36px;
  font-weight: 700;
  line-height: 140%; /* 50.4px */
  letter-spacing: -0.72px;
`;

const Currency = styled.span`
  color: var(--KTP-Grayscale-Gray_02, #111);
  font-size: 18px;
  font-weight: 600;
  line-height: 150%; /* 27px */
  letter-spacing: -0.36px;
`;

const DivideLine = styled.div`
  width: 100%;
  height: 9px;
  background: var(--KTP-Color-Mono-05, #f5f6f7);
`;

const ReceiptList = styled(FlexWrap)<{ isLatest: boolean }>`
  width: 100%;
  margin-top: 12px;
  align-items: center;
  flex-direction: ${(props) => (props.isLatest ? 'column' : 'column-reverse')};
  gap: 12px;
`;
